import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import {
  acceptEsignTerms,
  completeEsignIdentity,
  completeEsignPayment,
  fetchEsignProfile,
  formatToman,
  saveEsignAddress,
  saveEsignSignatureSvg,
  saveEsignUnlockPrefs,
  startEsignIdentity,
  submitEsignBirthDate,
  uploadEsignFaceMatch,
  verifyEsignOtp,
  type ESignProfile,
} from "../../lib/esign/api";
import { hashPattern, hashUnlockPassword } from "../../lib/esign/crypto";
import {
  loadBrowserCertificate,
  type StoredBrowserCertificate,
} from "../../lib/esign/browser-cert-store";
import {
  issueBrowserUserCertificate,
  type BrowserCertIssuePhase,
} from "../../lib/esign/issue-browser-certificate";
import {
  citiesForProvince,
  filterProvinces,
  IRAN_PROVINCES,
} from "../../lib/esign/iran-cities";
import { handleApiAuthError } from "../../lib/auth-required";
import { useAuth } from "../../contexts/auth-context";
import {
  LiveVideoCaptureModal,
  requestLiveCameraStream,
} from "../LiveVideoCaptureModal";
import {
  RECORD_SECONDS,
  faceCaptureFromFile,
  formatBirthDateInput,
  formatCardSerialInput,
  isCompleteBirthDate,
  isCompleteCardSerial,
  type FaceCapture,
} from "../../lib/face-capture";
import { PatternLock } from "./PatternLock";
import { SignaturePad } from "./SignaturePad";

type Step =
  | "terms"
  | "purchase"
  | "payment"
  | "payment-result"
  | "identity"
  | "address"
  | "password"
  | "draw"
  | "certificate"
  | "done";

type Props = {
  apiBase: string;
  onBack: () => void;
  onComplete: () => void;
};

const TERMS_INTRO = [
  "با فشردن دکمهٔ «تأیید شرایط»، این سند به‌منزلهٔ توافق‌نامهٔ الزام‌آور میان شما (کاربر/امضاکننده) و ارائه‌دهندهٔ خدمات نویساین تلقی می‌شود. لطفاً پیش از پذیرش، تمام بندها را با دقت مطالعه کنید.",
];

const TERMS_SECTIONS: Array<{ title: string; items: string[] }> = [
  {
    title: "۱. تعاریف",
    items: [
      "۱-۱. «نویساین»: سامانه و خدمات مرتبط با مدیریت اسناد، امضای الکترونیکی، احراز هویت و گواهی امضا.",
      "۱-۲. «کاربر»: شخص حقیقی که با حساب کاربری خود به خدمات احراز هویت و گواهی امضا دسترسی دارد.",
      "۱-۳. «احراز هویت»: فرایند تأیید هویت کاربر از طریق داده‌های هویتی، مالکیت سیم‌کارت، استعلام ثبت احوال و تطبیق چهره/ویدیوی زنده.",
      "۱-۴. «گواهی امضا»: گواهی یا زیرساخت رمزنگاری مرتبط با امضای الکترونیکی کاربر؛ از جمله گواهی ریشهٔ اختیاری مرتبط با مرکز ریشه (در صورت انتخاب و پشتیبانی).",
      "۱-۵. «امضای الکترونیکی»: عمل امضا یا تأیید سند از طریق زیرساخت امضای نویساین پس از تکمیل احراز هویت و تنظیمات امنیتی.",
      "۱-۶. «اطلاعات هویتی»: شامل کد ملی، شماره تلفن همراه، سریال پشت کارت ملی، تصویر/ویدیوی چهره، نشانی و سایر داده‌های لازم برای احراز هویت و صدور/فعال‌سازی گواهی.",
    ],
  },
  {
    title: "۲. موضوع و دامنهٔ خدمات",
    items: [
      "۲-۱. موضوع این شرایط، استفاده از خدمات احراز هویت، خرید/فعال‌سازی زیرساخت امضا، تنظیم رمز/الگوی باز کردن امضا، ثبت نمونهٔ امضای دستی و در صورت انتخاب، درخواست صدور گواهی مرتبط با مرکز ریشه است.",
      "۲-۲. این خدمات صرفاً برای امضا و تأیید اسناد در چارچوب پلتفرم نویساین و جریان‌های مجاز آن ارائه می‌شود و جایگزین مشاورهٔ حقوقی مستقل یا تأیید صحت محتوای اسناد نیست.",
      "۲-۳. نویساین ممکن است بخشی از مراحل (مانند درگاه پرداخت، استعلام‌ها یا صدور گواهی) را از طریق سرویس‌دهندگان ثالث یا به‌صورت آزمایشی/شبیه‌سازی‌شده در محیط توسعه ارائه کند؛ در هر حالت، نتیجهٔ نهایی منوط به موفقیت آن مرحله و قوانین جاری است.",
    ],
  },
  {
    title: "۳. شرایط استفاده و صلاحیت کاربر",
    items: [
      "۳-۱. کاربر باید شخص حقیقی دارای اهلیت قانونی باشد و صرفاً با هویت خود اقدام کند.",
      "۳-۲. ایجاد یا استفاده از حساب به نام شخص دیگر، ارائهٔ مدارک جعلی، یا دور زدن فرایند احراز هویت ممنوع است.",
      "۳-۳. استفاده از خدمات باید مطابق قوانین جمهوری اسلامی ایران، از جمله مقررات مربوط به تجارت الکترونیکی، امضای الکترونیکی، حفاظت از داده‌ها و جرائم رایانه‌ای باشد.",
      "۳-۴. کاربر متعهد است دستگاه، مرورگر و دسترسی‌های لازم (مانند دوربین و پیامک) را برای تکمیل فرایند در اختیار داشته باشد.",
    ],
  },
  {
    title: "۴. تعهدات و مسئولیت‌های کاربر",
    items: [
      "۴-۱. صحت، کامل بودن و به‌روز بودن تمام اطلاعات واردشده بر عهدهٔ کاربر است.",
      "۴-۲. کاربر مسئول حفظ محرمانگی رمز امضا، الگوی باز کردن، کدهای یک‌بارمصرف و دسترسی به دستگاه خود است و نباید آن‌ها را در اختیار دیگران قرار دهد.",
      "۴-۳. هر اقدامی که پس از احراز هویت و با استفاده از رمز/بیومتریک/الگوی کاربر روی اسناد انجام شود، منسوب به خود کاربر محسوب می‌شود مگر آنکه خلاف آن مطابق فرایندهای رسمی اثبات شود.",
      "۴-۴. کاربر موظف است در صورت مفقودی دستگاه، افشای رمز، یا ظن به دسترسی غیرمجاز، فوراً نسبت به تغییر رمز و اطلاع‌رسانی از مسیرهای پشتیبانی اقدام کند.",
      "۴-۵. کاربر حق ندارد از خدمات برای امضای اسناد غیرقانونی، متقلبانه، یا با هدف تضییع حقوق اشخاص ثالث استفاده کند.",
    ],
  },
  {
    title: "۵. فرایند احراز هویت و گواهی",
    items: [
      "۵-۱. احراز هویت ممکن است شامل تأیید کد ملی و شماره موبایل، دریافت و صحت‌سنجی رمز یک‌بارمصرف، استعلام سریال کارت ملی، دریافت تصویر مرجع ثبت احوال و ضبط/ارسال ویدیوی زنده برای تطبیق چهره باشد.",
      "۵-۲. رد شدن هر مرحله به‌معنای عدم امکان ادامه تا رفع نقص یا ارائهٔ اطلاعات صحیح است؛ نویساین موظف به پذیرش اطلاعات نادرست یا ناقص نیست.",
      "۵-۳. صدور یا فعال‌سازی گواهی ریشه/مرکز ریشه در صورت انتخاب کاربر و پرداخت هزینهٔ مربوطه انجام می‌شود و منوط به موفقیت احراز هویت و ضوابط فنی/قانونی مربوط است.",
      "۵-۴. نمونهٔ امضای دستی ثبت‌شده جنبهٔ نمایشی/تأییدی در اسناد دارد و جایگزین الزامات رمزنگاری امضای الکترونیکی معتبر نیست مگر آنکه در محصول به‌صراحت چنین اثری تعریف شده باشد.",
    ],
  },
  {
    title: "۶. پرداخت و هزینه‌ها",
    items: [
      "۶-۱. هزینه‌های احراز هویت، زیرساخت امضا و گزینه‌های اختیاری (مانند گواهی ریشه) مطابق تعرفه‌های نمایش‌داده‌شده در سامانه محاسبه و دریافت می‌شود.",
      "۶-۲. پرداخت موفق شرط ادامهٔ برخی مراحل است؛ پرداخت ناموفق یا ناتمام حقی برای فعال‌سازی کامل خدمات ایجاد نمی‌کند.",
      "۶-۳. در صورت بروز خطای فنی در پرداخت، کاربر باید رسید/پیگیری تراکنش را محفوظ بدارد و از پشتیبانی پیگیری کند.",
      "۶-۴. استرداد وجه صرفاً در چارچوب سیاست‌های اعلامی نویساین و قوانین لازم‌الاجرا بررسی می‌شود و موفقیت احراز هویت یا صدور گواهی را تضمین نمی‌کند.",
    ],
  },
  {
    title: "۷. حریم خصوصی و پردازش داده",
    items: [
      "۷-۱. اطلاعات هویتی و زیست‌سنجی صرفاً برای اهداف احراز هویت، امنیت امضا، جلوگیری از تقلب، ایفای الزامات قانونی و ارائهٔ خدمات مرتبط پردازش می‌شود.",
      "۷-۲. رمز امضا و الگوی باز کردن به‌صورت هش‌شده نگهداری می‌شود و نویساین متن خام رمز را ذخیره نمی‌کند.",
      "۷-۳. داده‌ها ممکن است برای تکمیل استعلام یا صدور گواهی در اختیار مراجع یا سرویس‌دهندگان مجاز قرار گیرد؛ این انتقال محدود به ضرورت انجام خدمت است.",
      "۷-۴. کاربر با پذیرش این شرایط، رضایت آگاهانهٔ خود را نسبت به پردازش داده‌های ضروری احراز هویت و امضا اعلام می‌کند.",
      "۷-۵. نگهداری، حذف یا ناشناس‌سازی داده‌ها مطابق سیاست حریم خصوصی نویساین و الزامات قانونی انجام می‌شود. درخواست‌های دسترسی یا حذف، در حدود قانون و نیاز به حفظ آثار امضا، بررسی خواهد شد.",
    ],
  },
  {
    title: "۸. امنیت",
    items: [
      "۸-۱. نویساین اقدامات متعارف فنی و سازمانی را برای حفاظت از داده‌ها و زیرساخت به کار می‌گیرد؛ با این حال هیچ سامانه‌ای مطلقاً عاری از ریسک نیست.",
      "۸-۲. کاربر باید از شبکه‌ها و دستگاه‌های قابل اعتماد استفاده کند و از نصب نرم‌افزارهای مشکوک خودداری نماید.",
      "۸-۳. هرگونه تلاش برای نفوذ، مهندسی معکوس غیرمجاز، استخراج کلیدها، یا اخلال در سرویس ممنوع است و می‌تواند به تعلیق حساب و پیگیری قانونی منجر شود.",
    ],
  },
  {
    title: "۹. محدودیت مسئولیت",
    items: [
      "۹-۱. نویساین صحت محتوای اسناد امضاشده، روابط حقوقی طرفین سند، یا نتایج اختلافات ناشی از مفاد قرارداد/سند را تضمین نمی‌کند.",
      "۹-۲. تأخیر یا اختلال ناشی از قطعی اینترنت، سرویس‌دهندگان ثالث، مراجع استعلام، یا قوهٔ قاهره خارج از مسئولیت مستقیم نویساین است مگر در حدود تقصیر اثبات‌شده.",
      "۹-۳. در حداکثر سقف مجاز قانونی، مسئولیت نویساین محدود به مبلغ پرداخت‌شده بابت همان خدمت در دورهٔ مرتبط خواهد بود؛ خسارات غیرمستقیم، از دست رفتن فرصت تجاری یا سود از دست‌رفته مشمول ضمانت نیست مگر حکم قانون خلاف آن باشد.",
      "۹-۴. مسئولیت پیامدهای ناشی از افشای رمز یا دسترسی غیرمجاز به دستگاه کاربر، بر عهدهٔ کاربر است.",
    ],
  },
  {
    title: "۱۰. تعلیق، خاتمه و تغییرات",
    items: [
      "۱۰-۱. نویساین می‌تواند در صورت نقض شرایط، ظن به تقلب، یا الزام قانونی، دسترسی به خدمات احراز هویت/امضا را موقتاً یا دائماً محدود کند.",
      "۱۰-۲. کاربر می‌تواند از ادامهٔ استفاده خودداری کند؛ خاتمهٔ استفاده، اثر امضاهای سابق و الزامات نگهداری قانونی را منتفی نمی‌کند.",
      "۱۰-۳. نویساین مجاز است این شرایط را به‌روزرسانی کند. ادامهٔ استفاده پس از انتشار نسخهٔ جدید، به معنای پذیرش تغییرات است مگر قانون شیوهٔ دیگری مقرر کرده باشد.",
    ],
  },
  {
    title: "۱۱. قانون حاکم و حل اختلاف",
    items: [
      "۱۱-۱. این شرایط تابع قوانین جمهوری اسلامی ایران است.",
      "۱۱-۲. اختلافات ابتدا از طریق پشتیبانی نویساین پیگیری می‌شود و در صورت عدم حصول توافق، مراجع صالح قضایی/قانونی محل فعالیت ارائه‌دهنده صالح به رسیدگی خواهند بود مگر توافق یا قانون خاص دیگری حاکم باشد.",
    ],
  },
  {
    title: "۱۲. پذیرش",
    items: [
      "۱۲-۱. با تأیید شرایط، کاربر اقرار می‌کند که متن را خوانده، فهمیده و با آن موافق است و اطلاعات ارائه‌شده صحیح و متعلق به خود اوست.",
      "۱۲-۲. اگر با تمام یا بخشی از شرایط موافق نیستید، از تأیید خودداری کنید و فرایند را متوقف نمایید؛ در این صورت امکان ادامهٔ احراز هویت و فعال‌سازی گواهی امضا وجود نخواهد داشت.",
    ],
  },
];

const AUTH_REQUIRED_MSG = "برای ادامه باید وارد حساب کاربری شوید.";

function toWesternDigits(value: string): string {
  const eastern = "۰۱۲۳۴۵۶۷۸۹";
  const arabic = "٠١٢٣٤٥٦٧٨٩";
  return value
    .replace(/[۰-۹]/g, (d) => String(eastern.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(arabic.indexOf(d)));
}

function digitsOnly(value: string): string {
  return toWesternDigits(value).replace(/\D/g, "");
}

function resolveStep(profile: ESignProfile | null): Step {
  if (!profile || !profile.termsAcceptedAt) return "terms";
  if (!profile.lastPaymentAmount) return "purchase";
  if (!profile.identityVerified) return "identity";
  if (!profile.province || !profile.city || !profile.address) return "address";
  if (!profile.hasUnlockPassword) return "password";
  if (!profile.hasSignatureSvg) return "draw";
  return "certificate";
}

const BROWSER_CERT_LIMITATION =
  "گواهی در همین مرورگر/سایت ذخیره می‌شود، نه در Certificate Store ویندوز/مک؛ پاک کردن داده‌های سایت کلید را از بین می‌برد.";

function certPhaseLabel(phase: BrowserCertIssuePhase): string {
  if (phase === "generating") return "در حال ساخت کلید در مرورگر…";
  if (phase === "submitting") return "در حال ارسال درخواست گواهی…";
  if (phase === "polling") return "در انتظار صدور گواهی…";
  return "در حال ذخیره در این مرورگر…";
}

export function EsignOnboardFlow({ apiBase, onBack, onComplete }: Props) {
  const { user, isAuthenticated, openLogin } = useAuth();
  const [profile, setProfile] = useState<ESignProfile | null>(null);
  const [step, setStep] = useState<Step>("terms");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [includeRoot, setIncludeRoot] = useState(true);
  const [paymentOk, setPaymentOk] = useState<boolean | null>(null);

  const [nationalId, setNationalId] = useState("");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [otp, setOtp] = useState("");
  const [debugCode, setDebugCode] = useState<string | null>(null);
  const [birthDate, setBirthDate] = useState("");
  const [cardSerial, setCardSerial] = useState("");
  const kycLine = profile?.kycVideoLine ?? "تأیید درخواست احراز هویت در سامانه نویساین";
  const [captureOpen, setCaptureOpen] = useState(false);
  const [cameraBootstrap, setCameraBootstrap] =
    useState<Promise<MediaStream> | null>(null);
  const videoRef = useRef<HTMLInputElement>(null);

  const openLiveCapture = () => {
    setError(null);
    setCameraBootstrap(requestLiveCameraStream());
    setCaptureOpen(true);
  };

  const closeLiveCapture = () => {
    setCaptureOpen(false);
    setCameraBootstrap(null);
  };

  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [provinceQuery, setProvinceQuery] = useState("");
  const [cityQuery, setCityQuery] = useState("");

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [fingerprintEnabled, setFingerprintEnabled] = useState(false);
  const [patternEnabled, setPatternEnabled] = useState(false);
  const [patternDots, setPatternDots] = useState<number[] | null>(null);
  const [showPatternSet, setShowPatternSet] = useState(false);

  const [signatureSvg, setSignatureSvg] = useState<string | null>(null);
  const [browserCert, setBrowserCert] = useState<StoredBrowserCertificate | null>(
    null,
  );
  const [certPhase, setCertPhase] = useState<BrowserCertIssuePhase | null>(null);
  const certAbortRef = useRef<AbortController | null>(null);
  const loginPromptedRef = useRef(false);

  const requestRelogin = (retry?: () => void) => {
    setError(AUTH_REQUIRED_MSG);
    if (loginPromptedRef.current) return;
    loginPromptedRef.current = true;
    openLogin(() => {
      loginPromptedRef.current = false;
      setError(null);
      if (retry) retry();
      else setReloadToken((n) => n + 1);
    });
  };

  const reportError = (e: unknown, fallback: string, retry?: () => void) => {
    if (
      handleApiAuthError(e, () => {
        loginPromptedRef.current = false;
        setError(null);
        if (retry) retry();
        else setReloadToken((n) => n + 1);
      })
    ) {
      loginPromptedRef.current = true;
      setError(AUTH_REQUIRED_MSG);
      return;
    }
    setError(e instanceof Error ? e.message : fallback);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      if (!isAuthenticated) {
        if (!cancelled) {
          setLoading(false);
          setStep("terms");
          requestRelogin();
        }
        return;
      }
      try {
        const p = await fetchEsignProfile(apiBase);
        if (cancelled) return;
        setProfile(p);
        setIncludeRoot(true);
        if (p.province) setProvince(p.province);
        if (p.city) setCity(p.city);
        if (p.address) setAddress(p.address);
        setStep(resolveStep(p));
      } catch (e) {
        if (!cancelled) {
          reportError(e, "بارگذاری پروفایل امضا ناموفق بود");
          setStep("terms");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // reloadToken / isAuthenticated intentionally re-fetch after login
  }, [apiBase, isAuthenticated, reloadToken]);

  useEffect(() => {
    if (!user?.id) {
      setBrowserCert(null);
      return;
    }
    let cancelled = false;
    void loadBrowserCertificate(user.id).then((rec) => {
      if (!cancelled) setBrowserCert(rec);
    });
    return () => {
      cancelled = true;
      certAbortRef.current?.abort();
    };
  }, [user?.id]);

  const issueCertRequired =
    includeRoot || Boolean(profile?.lastPaymentIncludedRootCert);

  const runBrowserIssuance = () => {
    if (!user?.id || busy) return;
    const commonName =
      [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
      user.fullName.trim() ||
      nationalId.trim() ||
      "Nevisign User";
    certAbortRef.current?.abort();
    const ac = new AbortController();
    certAbortRef.current = ac;
    void (async () => {
      setBusy(true);
      setError(null);
      setCertPhase("generating");
      try {
        // The CA binds the certificate to the server-verified national code only.
        const verifiedNationalId =
          profile?.verifiedNationalId ?? (await fetchEsignProfile(apiBase)).verifiedNationalId;
        if (!verifiedNationalId) {
          throw new Error("پیش از صدور گواهی، احراز هویت را کامل کنید");
        }
        const rec = await issueBrowserUserCertificate({
          apiBase,
          userId: user.id,
          commonName,
          nationalId: verifiedNationalId,
          signal: ac.signal,
          onPhase: (phase) => setCertPhase(phase),
        });
        setBrowserCert(rec);
        try {
          applyProfile(await fetchEsignProfile(apiBase));
        } catch {
          /* local cert is the source of truth for this browser */
        }
        setStep("done");
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        reportError(e, "صدور گواهی ناموفق بود");
      } finally {
        setCertPhase(null);
        setBusy(false);
      }
    })();
  };

  const pricing = profile?.pricing;
  const baseTotal =
    (pricing?.identityVerificationToman ?? 85_000) +
    (pricing?.signatureInfrastructureToman ?? 45_000);
  const rootPrice = pricing?.rootCertificateToman ?? 40_000;
  const total = baseTotal + (includeRoot ? rootPrice : 0);

  const provinceOptions = useMemo(
    () => filterProvinces(provinceQuery),
    [provinceQuery],
  );
  const cityOptions = useMemo(
    () => citiesForProvince(province, cityQuery),
    [province, cityQuery],
  );

  const applyProfile = (p: ESignProfile) => {
    setProfile(p);
    if (p.debugCode) setDebugCode(p.debugCode);
  };

  const onFaceMatch = async (capture: FaceCapture) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const p = await uploadEsignFaceMatch(apiBase, capture);
      applyProfile(p);
      await completeEsignIdentity(apiBase);
      closeLiveCapture();
      setStep("address");
    } catch (err) {
      reportError(err, "احراز هویت ویدیویی ناموفق بود");
      // A wrong birth date sends the session back to the birth-date step.
      try {
        applyProfile(await fetchEsignProfile(apiBase));
      } catch {
        /* keep the error above; profile refresh is best-effort */
      }
      throw err instanceof Error ? err : new Error("احراز هویت ویدیویی ناموفق بود");
    } finally {
      setBusy(false);
    }
  };

  const onPickVideo = async (file: File | null) => {
    if (videoRef.current) videoRef.current.value = "";
    if (!file || busy) return;
    setError(null);
    let capture: FaceCapture;
    try {
      capture = await faceCaptureFromFile(file);
    } catch (e) {
      setError(e instanceof Error ? e.message : "پردازش ویدیو ناموفق بود");
      return;
    }
    await onFaceMatch(capture).catch(() => undefined);
  };

  const sticky = (
    label: string,
    onClick: () => void,
    disabled = false,
  ) => (
    <div className="esign-sticky">
      <button
        type="button"
        className="primary-btn wide"
        disabled={disabled || busy}
        onClick={onClick}
      >
        {busy ? "لطفاً صبر کنید…" : label}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="esign-flow">
        <p className="signing-muted">در حال بارگذاری…</p>
      </div>
    );
  }

  return (
    <div className={`esign-flow${step === "terms" ? " esign-flow--terms" : ""}`}>
      <div className="esign-flow-top">
        <div className="esign-flow-title-row">
          <button
            type="button"
            className="icon-btn compact esign-back-btn"
            aria-label="بازگشت"
            title="بازگشت"
            onClick={onBack}
          >
            <ArrowRight size={18} aria-hidden />
          </button>
          <h1 className="esign-flow-title">احراز هویت و گواهی امضا</h1>
        </div>
      </div>

      {error === AUTH_REQUIRED_MSG ? (
        <div className="signing-error esign-auth-needed" role="alert">
          <p>{error}</p>
          <button
            type="button"
            className="primary-btn"
            onClick={() => {
              loginPromptedRef.current = false;
              requestRelogin();
            }}
          >
            ورود به حساب
          </button>
        </div>
      ) : error ? (
        <p className="signing-error">{error}</p>
      ) : null}

      {step === "terms" ? (
        <div className="esign-terms-step">
          <header className="esign-terms-header">
            <h2>شرایط استفاده از خدمات</h2>
            <p className="esign-terms-meta">آخرین به‌روزرسانی: مرداد ۱۴۰۴</p>
          </header>
          <div className="esign-terms" tabIndex={0}>
            {TERMS_INTRO.map((p) => (
              <p key={p.slice(0, 24)} className="esign-terms-lead">
                {p}
              </p>
            ))}
            {TERMS_SECTIONS.map((section) => (
              <section key={section.title} className="esign-terms-section">
                <h3>{section.title}</h3>
                <ul>
                  {section.items.map((item) => (
                    <li key={item.slice(0, 32)}>{item}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
          {sticky("تأیید شرایط", () => {
            void (async () => {
              setBusy(true);
              setError(null);
              try {
                const p = await acceptEsignTerms(apiBase);
                applyProfile(p);
                setStep("purchase");
              } catch (e) {
                reportError(e, "خطا");
              } finally {
                setBusy(false);
              }
            })();
          })}
        </div>
      ) : null}

      {step === "purchase" && pricing ? (
        <div className="esign-card">
          <h2>خرید قابلیت احراز هویت</h2>
          <div className="esign-price-box">
            <p className="esign-price-title">احراز هویت برای امضای اسناد</p>
            <p className="signing-muted">
              برای احراز هویت به سریال پشت کارت ملی و ضبط ویدیوی زنده نیاز است.
            </p>
            <ul className="esign-price-list">
              <li>
                احراز هویت
                <strong>{formatToman(pricing.identityVerificationToman)} تومان</strong>
              </li>
              <li>
                زیرساخت امضا
                <strong>
                  {formatToman(pricing.signatureInfrastructureToman)} تومان
                </strong>
              </li>
            </ul>
          </div>
          <label className={`esign-price-box optional${includeRoot ? " on" : ""}`}>
            <div className="esign-toggle-row">
              <input
                type="checkbox"
                checked={includeRoot}
                onChange={(e) => setIncludeRoot(e.target.checked)}
              />
              <div>
                <p className="esign-price-title">
                  صدور گواهی امضای مرکز ریشه وزارت صمت
                </p>
                <p className="signing-muted">
                  {formatToman(rootPrice)} تومان — اختیاری
                </p>
              </div>
            </div>
          </label>
          {sticky(`پرداخت ${formatToman(total)} تومان و ادامه`, () => {
            setPaymentOk(null);
            setStep("payment");
          })}
        </div>
      ) : null}

      {step === "payment" ? (
        <div className="esign-card">
          <h2>درگاه پرداخت (شبیه‌سازی)</h2>
          <p className="signing-muted">
            مبلغ قابل پرداخت: {formatToman(total)} تومان
          </p>
          <div className="esign-pay-actions">
            <button
              type="button"
              className="primary-btn"
              disabled={busy}
              onClick={() => {
                void (async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    const p = await completeEsignPayment(apiBase, {
                      success: true,
                      includeRootCertificate: includeRoot,
                    });
                    applyProfile(p);
                    setPaymentOk(true);
                    setStep("payment-result");
                  } catch (e) {
                    reportError(e, "خطا");
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            >
              شبیه‌سازی پرداخت موفق
            </button>
            <button
              type="button"
              className="ghost-btn"
              disabled={busy}
              onClick={() => {
                void (async () => {
                  setBusy(true);
                  try {
                    await completeEsignPayment(apiBase, {
                      success: false,
                      includeRootCertificate: includeRoot,
                    });
                    setPaymentOk(false);
                    setStep("payment-result");
                  } catch (e) {
                    reportError(e, "خطا");
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            >
              شبیه‌سازی پرداخت ناموفق
            </button>
          </div>
        </div>
      ) : null}

      {step === "payment-result" ? (
        <div className="esign-card">
          {paymentOk ? (
            <>
              <div className="signing-success">
                <CheckCircle2 size={32} className="signing-success-icon" />
                <p className="signing-success-title">پرداخت موفق بود</p>
                <p className="signing-muted">
                  {includeRoot
                    ? "گواهی ریشه وزارت صمت نیز در وضعیت stub ثبت شد."
                    : "می‌توانید احراز هویت را ادامه دهید."}
                </p>
              </div>
              {sticky("ادامه احراز هویت", () => setStep("identity"))}
            </>
          ) : (
            <>
              <p className="signing-error">پرداخت ناموفق بود</p>
              {sticky("بازگشت به صفحه خرید", () => setStep("purchase"))}
            </>
          )}
        </div>
      ) : null}

      {step === "identity" ? (
        <div className="esign-card">
          <h2>احراز هویت</h2>
          <p className="signing-muted">
            همان الگوی درگاه امضا: کد ملی و OTP، سریال کارت، سپس ویدیو.
          </p>
          <div className="signing-form">
            {(profile?.identityStep === "idle" ||
              profile?.identityStep === "collect" ||
              !profile?.identityStep) && (
              <>
                <label className="signing-field">
                  کد ملی
                  <input
                    inputMode="numeric"
                    value={nationalId}
                    onChange={(e) => setNationalId(digitsOnly(e.target.value))}
                  />
                </label>
                <label className="signing-field">
                  شماره تماس
                  <input
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(toWesternDigits(e.target.value))}
                  />
                </label>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={busy}
                  onClick={() => {
                    void (async () => {
                      setBusy(true);
                      setError(null);
                      try {
                        const p = await startEsignIdentity(apiBase, {
                          nationalId,
                          phone,
                        });
                        applyProfile(p);
                        setDebugCode(p.debugCode ?? null);
                      } catch (e) {
                        reportError(e, "خطا");
                      } finally {
                        setBusy(false);
                      }
                    })();
                  }}
                >
                  ادامه و دریافت کد
                </button>
              </>
            )}

            {profile?.identityStep === "otp_sent" ? (
              <>
                <label className="signing-field">
                  کد تأیید پیامک
                  <input
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => setOtp(digitsOnly(e.target.value))}
                  />
                </label>
                {debugCode ? (
                  <p className="signing-debug">حالت توسعه — کد: {debugCode}</p>
                ) : null}
                <button
                  type="button"
                  className="primary-btn"
                  disabled={busy}
                  onClick={() => {
                    void (async () => {
                      setBusy(true);
                      setError(null);
                      try {
                        applyProfile(await verifyEsignOtp(apiBase, otp));
                      } catch (e) {
                        reportError(e, "خطا");
                        setOtp("");
                        try {
                          applyProfile(await fetchEsignProfile(apiBase));
                        } catch {
                          /* keep the error above; profile refresh is best-effort */
                        }
                      } finally {
                        setBusy(false);
                      }
                    })();
                  }}
                >
                  تأیید مالکیت سیم‌کارت
                </button>
              </>
            ) : null}

            {profile?.identityStep === "otp_verified" ? (
              <>
                <label className="signing-field">
                  تاریخ تولد (شمسی)
                  <input
                    inputMode="numeric"
                    dir="ltr"
                    autoComplete="bday"
                    value={birthDate}
                    onChange={(e) => setBirthDate(formatBirthDateInput(e.target.value))}
                    placeholder="1370/06/21"
                  />
                </label>
                <label className="signing-field">
                  سریال پشت کارت ملی هوشمند
                  <input
                    dir="ltr"
                    autoCapitalize="characters"
                    autoComplete="off"
                    spellCheck={false}
                    value={cardSerial}
                    onChange={(e) => setCardSerial(formatCardSerialInput(e.target.value))}
                    placeholder="1M20115996"
                  />
                </label>
                <p className="signing-muted small">
                  تاریخ تولد مطابق کارت ملی و سریال ۱۰ نویسه‌ای پشت کارت؛ همراه ویدیوی شما برای
                  استعلام ثبت احوال ارسال می‌شود.
                </p>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={busy || !isCompleteBirthDate(birthDate) || !isCompleteCardSerial(cardSerial)}
                  onClick={() => {
                    void (async () => {
                      setBusy(true);
                      setError(null);
                      try {
                        applyProfile(await submitEsignBirthDate(apiBase, birthDate, cardSerial));
                      } catch (e) {
                        reportError(e, "خطا");
                      } finally {
                        setBusy(false);
                      }
                    })();
                  }}
                >
                  ادامه
                </button>
              </>
            ) : null}

            {profile?.identityStep === "civil_verified" ? (
              <>
                <p className="signing-muted">
                  یک ویدیوی کوتاه (حداکثر {RECORD_SECONDS} ثانیه) با صدا ضبط کنید و در آن این جمله
                  را بلند بخوانید:
                </p>
                <p className="live-capture-line">«{kycLine}»</p>
                <p className="signing-muted">
                  چهره با تصویر ثبت احوال و گفتار با همین جمله تطبیق داده می‌شود.
                </p>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={busy}
                  onClick={openLiveCapture}
                >
                  {busy ? "در حال احراز هویت…" : "ضبط ویدیو زنده"}
                </button>
                <input
                  ref={videoRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/*"
                  capture="user"
                  className="hidden"
                  onChange={(e) => void onPickVideo(e.target.files?.[0] ?? null)}
                />
                <button
                  type="button"
                  className="ghost-btn live-capture-full"
                  disabled={busy}
                  onClick={() => videoRef.current?.click()}
                >
                  ضبط با دوربین گوشی / انتخاب فایل
                </button>
                <p className="signing-muted small">
                  ویدیو باید کوتاه (حداکثر ۱۰ مگابایت)، با صدا، و چهره در آن کامل و واضح باشد.
                </p>
                <LiveVideoCaptureModal
                  open={captureOpen}
                  busy={busy}
                  line={kycLine}
                  bootstrapStream={cameraBootstrap}
                  confirmLabel="تأیید و ادامه"
                  busyLabel="در حال احراز هویت…"
                  onClose={closeLiveCapture}
                  onConfirm={(capture) => onFaceMatch(capture)}
                />
              </>
            ) : null}

            {profile?.identityStep === "face_matched" ||
            profile?.identityVerified ? (
              sticky("ادامه", () => {
                void (async () => {
                  setBusy(true);
                  try {
                    applyProfile(await completeEsignIdentity(apiBase));
                    setStep("address");
                  } catch (e) {
                    reportError(e, "خطا");
                  } finally {
                    setBusy(false);
                  }
                })();
              })
            ) : null}
          </div>
        </div>
      ) : null}

      {step === "address" ? (
        <div className="esign-card">
          <h2>آدرس</h2>
          <div className="signing-form">
            <label className="signing-field">
              استان
              <input
                value={provinceQuery || province}
                onChange={(e) => {
                  setProvinceQuery(e.target.value);
                  setProvince("");
                  setCity("");
                }}
                onBlur={() => {
                  const match = IRAN_PROVINCES.find(
                    (p) => p.name === provinceQuery.trim(),
                  );
                  if (match) {
                    setProvince(match.name);
                    setProvinceQuery(match.name);
                  }
                }}
                placeholder="جستجوی استان"
                list="esign-provinces"
              />
              <datalist id="esign-provinces">
                {provinceOptions.map((p) => (
                  <option key={p.name} value={p.name} />
                ))}
              </datalist>
            </label>
            {!province && provinceQuery ? (
              <ul className="esign-suggest">
                {provinceOptions.slice(0, 8).map((p) => (
                  <li key={p.name}>
                    <button
                      type="button"
                      onClick={() => {
                        setProvince(p.name);
                        setProvinceQuery(p.name);
                        setCity("");
                        setCityQuery("");
                      }}
                    >
                      {p.name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <label className="signing-field">
              شهر
              <input
                value={cityQuery || city}
                disabled={!province}
                onChange={(e) => {
                  setCityQuery(e.target.value);
                  setCity("");
                }}
                onBlur={() => {
                  const match = citiesForProvince(province).find(
                    (c) => c === cityQuery.trim(),
                  );
                  if (match) {
                    setCity(match);
                    setCityQuery(match);
                  }
                }}
                placeholder={province ? "جستجوی شهر" : "ابتدا استان را انتخاب کنید"}
                list="esign-cities"
              />
              <datalist id="esign-cities">
                {cityOptions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </label>
            {province && !city && cityQuery ? (
              <ul className="esign-suggest">
                {cityOptions.slice(0, 10).map((c) => (
                  <li key={c}>
                    <button
                      type="button"
                      onClick={() => {
                        setCity(c);
                        setCityQuery(c);
                      }}
                    >
                      {c}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {!province && !provinceQuery ? (
              <ul className="esign-suggest compact">
                {IRAN_PROVINCES.slice(0, 6).map((p) => (
                  <li key={p.name}>
                    <button
                      type="button"
                      onClick={() => {
                        setProvince(p.name);
                        setProvinceQuery(p.name);
                      }}
                    >
                      {p.name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <label className="signing-field">
              نشانی کامل
              <textarea
                rows={3}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="خیابان، پلاک، واحد…"
              />
            </label>
          </div>
          {sticky(
            "ذخیره و ادامه",
            () => {
              void (async () => {
                setBusy(true);
                setError(null);
                try {
                  applyProfile(
                    await saveEsignAddress(apiBase, {
                      province,
                      city,
                      address,
                    }),
                  );
                  setStep("password");
                } catch (e) {
                  reportError(e, "خطا");
                } finally {
                  setBusy(false);
                }
              })();
            },
            !province || !city || address.trim().length < 5,
          )}
        </div>
      ) : null}

      {step === "password" ? (
        <div className="esign-card">
          <h2>رمز امضا</h2>
          <p className="signing-muted">
            رمز به‌صورت SHA-256 در دستگاه هش می‌شود و فقط هش ذخیره می‌گردد.
          </p>
          <div className="signing-form">
            <label className="signing-field">
              رمز امضا
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label className="signing-field">
              تکرار رمز
              <input
                type="password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
              />
            </label>
            <label className="esign-check">
              <input
                type="checkbox"
                checked={fingerprintEnabled}
                onChange={(e) => setFingerprintEnabled(e.target.checked)}
              />
              فعال‌سازی اثرانگشت / بیومتریک (در صورت پشتیبانی مرورگر)
            </label>
            <label className="esign-check">
              <input
                type="checkbox"
                checked={patternEnabled}
                onChange={(e) => {
                  setPatternEnabled(e.target.checked);
                  if (e.target.checked) setShowPatternSet(true);
                  else {
                    setPatternDots(null);
                    setShowPatternSet(false);
                  }
                }}
              />
              فعال‌سازی الگوی باز کردن گوشی
            </label>
            {showPatternSet ? (
              <PatternLock
                mode="set"
                onCancel={() => {
                  setShowPatternSet(false);
                  setPatternEnabled(false);
                  setPatternDots(null);
                }}
                onComplete={(dots) => {
                  setPatternDots(dots);
                  setShowPatternSet(false);
                }}
              />
            ) : null}
            {patternEnabled && patternDots ? (
              <p className="signing-muted small">الگو ثبت شد ({patternDots.length} نقطه)</p>
            ) : null}
          </div>
          {sticky(
            "ذخیره و ادامه",
            () => {
              if (password.length < 4) {
                setError("رمز حداقل ۴ کاراکتر باشد");
                return;
              }
              if (password !== password2) {
                setError("تکرار رمز یکسان نیست");
                return;
              }
              if (patternEnabled && !patternDots) {
                setError("الگوی باز کردن را رسم کنید");
                return;
              }
              void (async () => {
                setBusy(true);
                setError(null);
                try {
                  const unlockPasswordHash = await hashUnlockPassword(password);
                  const patternHash =
                    patternEnabled && patternDots
                      ? await hashPattern(patternDots)
                      : null;
                  applyProfile(
                    await saveEsignUnlockPrefs(apiBase, {
                      unlockPasswordHash,
                      fingerprintEnabled,
                      patternEnabled,
                      patternHash,
                    }),
                  );
                  setStep("draw");
                } catch (e) {
                  reportError(e, "خطا");
                } finally {
                  setBusy(false);
                }
              })();
            },
            false,
          )}
        </div>
      ) : null}

      {step === "draw" ? (
        <div className="esign-card">
          <h2>رسم امضا</h2>
          <p className="signing-muted">امضای دستی به‌صورت SVG ذخیره می‌شود.</p>
          <SignaturePad onChange={setSignatureSvg} />
          {sticky(
            "ذخیره امضا و ادامه",
            () => {
              if (!signatureSvg) {
                setError("ابتدا امضا را رسم کنید");
                return;
              }
              void (async () => {
                setBusy(true);
                setError(null);
                try {
                  applyProfile(
                    await saveEsignSignatureSvg(apiBase, signatureSvg),
                  );
                  setStep("certificate");
                } catch (e) {
                  reportError(e, "خطا");
                } finally {
                  setBusy(false);
                }
              })();
            },
            !signatureSvg,
          )}
        </div>
      ) : null}

      {step === "certificate" ? (
        <div className="esign-card">
          <h2>گواهی امضا در مرورگر</h2>
          <p className="signing-muted">
            کلید خصوصی در همین مرورگر ساخته می‌شود و هرگز به سرور فرستاده نمی‌شود.
            اپ بومی بعداً می‌تواند کلید را در Secure Enclave یا Keystore نگه دارد.
          </p>
          <p className="esign-browser-limit">{BROWSER_CERT_LIMITATION}</p>
          {browserCert ? (
            <>
              <div className="signing-success">
                <CheckCircle2 size={32} className="signing-success-icon" />
                <p className="signing-success-title">گواهی این مرورگر آماده است</p>
                <p className="signing-muted esign-cert-meta">
                  {browserCert.serialNumber
                    ? `شماره سریال: ${browserCert.serialNumber}`
                    : "صادر شده"}
                  {browserCert.notAfter
                    ? ` · اعتبار تا ${new Date(browserCert.notAfter).toLocaleDateString("fa-IR")}`
                    : ""}
                </p>
              </div>
              {sticky("ادامه", () => setStep("done"))}
            </>
          ) : (
            <>
              {certPhase ? (
                <p className="signing-muted">{certPhaseLabel(certPhase)}</p>
              ) : null}
              {sticky(
                certPhase
                  ? certPhaseLabel(certPhase)
                  : "صدور گواهی در این مرورگر",
                () => runBrowserIssuance(),
              )}
              {!issueCertRequired ? (
                <button
                  type="button"
                  className="ghost-btn live-capture-full"
                  disabled={busy}
                  onClick={() => setStep("done")}
                >
                  فعلاً رد شو
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {step === "done" ? (
        <div className="esign-card">
          <div className="signing-success">
            <CheckCircle2 size={36} className="signing-success-icon" />
            <p className="signing-success-title">احراز هویت کامل شد</p>
            <p className="signing-muted">
              {browserCert
                ? "گواهی امضا در همین مرورگر ذخیره شد و برای امضا با کلید همین سایت آماده است."
                : profile?.hasRootCertificate
                  ? "روی سرور گواهی ثبت شده، اما کلید این مرورگر موجود نیست — در صورت نیاز دوباره صادر کنید."
                  : "می‌توانید بعداً گواهی را در همین مرورگر صادر کنید."}
            </p>
          </div>
          <p className="esign-browser-limit">{BROWSER_CERT_LIMITATION}</p>
          {!browserCert ? (
            <button
              type="button"
              className="ghost-btn live-capture-full"
              disabled={busy}
              onClick={() => setStep("certificate")}
            >
              صدور گواهی در این مرورگر
            </button>
          ) : null}
          {sticky("بازگشت به داشبورد", () => onComplete())}
        </div>
      ) : null}
    </div>
  );
}
